"use client";
import React, { useEffect, useState } from "react";
import { Menu, X, Home, Tag, Package, Store, Download, ChevronRight } from "lucide-react";
import Link from "components/common/Link";
import LanguageSwitcher from "components/common/LanguageSwitcher";
import { useT } from "providers/I18nProvider";
import useStoreUrl from "hooks/useStoreUrl";
import { FEATURE_PREPAID_PACKAGE } from "utils/constant";

/**
 * Menu mobile (< md): nút hamburger góc PHẢI (vùng ngón cái), drawer trượt từ phải.
 * Gom các mục desktop đang ẩn trên mobile: Ưu đãi, Gói tiết kiệm, Hợp tác, Tải App, ngôn ngữ.
 * Tải App trên điện thoại → mở thẳng store theo thiết bị (QR vô nghĩa trên chính điện thoại).
 */
const MobileMenu = () => {
  const t = useT();
  const [open, setOpen] = useState(false);
  const storeUrl = useStoreUrl();

  // Khoá cuộn nền + đóng bằng Esc khi drawer mở
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const items = [
    { href: "/", icon: Home, label: t("header.menuHome") },
    { href: "/uu-dai", icon: Tag, label: t("header.menuOffers") },
    ...(FEATURE_PREPAID_PACKAGE
      ? [{ href: "/goi-tiet-kiem", icon: Package, label: t("header.menuPackages") }]
      : []),
    { href: "/hop-tac", icon: Store, label: t("header.partner"), hint: t("header.partnerHint") },
  ];

  return (
    <>
      <button
        type="button"
        aria-label={t("header.menu")}
        aria-expanded={open}
        onClick={() => setOpen(true)}
        className="md:hidden -mr-2 flex h-11 w-11 items-center justify-center rounded-full text-white active:bg-white/20"
      >
        <Menu size={26} />
      </button>

      <div
        className={`md:hidden fixed inset-0 z-[1000] ${open ? "" : "pointer-events-none"}`}
        aria-hidden={!open}
        // Khi đóng: aria-hidden=true nhưng các Link/button bên trong vẫn ở DOM và
        // bắt được Tab (pointer-events-none chỉ chặn chuột) → "aria-hidden-focus"
        // (axe). `inert` tắt hẳn focus + tương tác cho mọi phần tử con khi đóng.
        inert={!open}
      >
        <div
          onClick={() => setOpen(false)}
          className={`absolute inset-0 bg-black/40 transition-opacity duration-200 ${open ? "opacity-100" : "opacity-0"}`}
        />
        <nav
          role="dialog"
          aria-modal="true"
          aria-label={t("header.menu")}
          className={`absolute right-0 top-0 flex h-full w-[min(84vw,340px)] flex-col bg-white shadow-2xl transition-transform duration-250 ease-out motion-reduce:transition-none ${
            open ? "translate-x-0" : "translate-x-full"
          }`}
          style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
        >
          <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
            <span className="text-base font-semibold text-gray-900">{t("header.menu")}</span>
            <button
              type="button"
              aria-label={t("header.close")}
              onClick={() => setOpen(false)}
              className="-mr-2 flex h-11 w-11 items-center justify-center rounded-full text-gray-600 active:bg-gray-100"
            >
              <X size={24} />
            </button>
          </div>

          <ul className="flex-1 overflow-y-auto py-2">
            {items.map(({ href, icon: Icon, label, hint }) => (
              <li key={href}>
                <Link
                  href={href}
                  onClick={() => setOpen(false)}
                  className="flex min-h-[52px] items-center gap-3 px-5 py-2 text-[16px] text-gray-800 active:bg-gray-50"
                >
                  <Icon size={20} className="shrink-0 text-primary" />
                  <span className="flex-1">
                    <span className="block">{label}</span>
                    {hint && <span className="block text-[13px] text-gray-500">{hint}</span>}
                  </span>
                  <ChevronRight size={18} className="text-gray-300" />
                </Link>
              </li>
            ))}
          </ul>

          <div className="border-t border-gray-100 px-5 py-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-[14px] text-gray-600">{t("header.language")}</span>
              <div className="rounded-full bg-primary px-0.5 py-0.5">
                <LanguageSwitcher />
              </div>
            </div>
            <a
              href={storeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex h-12 w-full items-center justify-center gap-2 rounded-full text-[16px] font-semibold active:opacity-90"
              style={{ backgroundColor: "var(--primary)", color: "#fff" }}
            >
              <Download size={18} />
              {t("header.downloadApp")}
            </a>
          </div>
        </nav>
      </div>
    </>
  );
};

export default MobileMenu;
